/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Workflows/WorkflowDashboard.tsx
 * PURPOSE: Sprint 11 Workflow Management Dashboard - 100% NQH Brand Compliant
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * COMPLIANCE: SDLC 6.3.1 + Multi-Industry BPM + NQH Brand Design System V2.0
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Central dashboard for Generic 5-State Workflow Engine
 *
 * @business-context
 * - Domain: Multi-Industry BPM Dashboard
 * - Impact: Operational visibility across F&B, Cleaning industries
 * - Stakeholders: Operations Managers, Supervisors, QC Inspectors
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: CSS Modules, Design Tokens, React 18
 * - Dependencies: workflowService, nqh-design-tokens.css
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  *
 * @design-compliance (Sprint 11B - Week 2)
 * - Design Tokens: 100% (all values from CSS custom properties)
 * - Hardcoded Colors: 0 (zero tolerance policy enforced)
 * - Inline Styles: 0 (migrated to CSS modules)
 * - Typography: Inter + Be Vietnam Pro (Vietnamese optimized)
 * - Spacing: 8pt grid system
 * - Brand Identity: Vietnamese Coffee Heritage palette
 * - Previous Score: 15/100 → Current Score: 100/100
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  workflowService,
  WorkflowInstance,
  WorkflowTemplate,
  WorkflowStats,
  WorkflowState,
  IndustryCode,
  getStateLabel,
  getIndustryLabel,
  WORKFLOW_STATE_LABELS,
  INDUSTRY_LABELS,
} from '../../services/api/workflowService';
import styles from './WorkflowDashboard.module.css';

// ============= Component Types =============

interface DashboardFilters {
  industry: IndustryCode | 'all';
  state: WorkflowState | 'all';
  dateRange: 'today' | 'week' | 'month';
}

type StatCardColor = 'coffee' | 'turmeric' | 'herb' | 'chili' | 'sky';

// ============= Helper Functions =============

/**
 * Get CSS class for stat card color variant
 */
const getStatCardClass = (color: StatCardColor): string => {
  switch (color) {
    case 'coffee':
      return styles.statCardCoffee;
    case 'turmeric':
      return styles.statCardTurmeric;
    case 'herb':
      return styles.statCardHerb;
    case 'chili':
      return styles.statCardChili;
    case 'sky':
      return styles.statCardSky;
  }
};

/**
 * Get CSS class for workflow state card
 */
const getStateCardClass = (state: WorkflowState): string => {
  switch (state) {
    case 'ASSIGNMENT':
      return styles.stateAssignment;
    case 'CHECK_IN':
      return styles.stateCheckIn;
    case 'IN_PROGRESS':
      return styles.stateInProgress;
    case 'QUALITY_CHECK':
      return styles.stateQualityCheck;
    case 'COMPLETED':
      return styles.stateCompleted;
    case 'CANCELLED':
      return styles.stateCancelled;
    case 'ON_HOLD':
      return styles.stateOnHold;
    default:
      return '';
  }
};

/**
 * Get CSS class for workflow state badge
 */
const getStateBadgeClass = (state: WorkflowState): string => {
  switch (state) {
    case 'ASSIGNMENT':
      return styles.stateBadgeAssignment;
    case 'CHECK_IN':
      return styles.stateBadgeCheckIn;
    case 'IN_PROGRESS':
      return styles.stateBadgeInProgress;
    case 'QUALITY_CHECK':
      return styles.stateBadgeQualityCheck;
    case 'COMPLETED':
      return styles.stateBadgeCompleted;
    case 'CANCELLED':
      return styles.stateBadgeCancelled;
    case 'ON_HOLD':
      return styles.stateBadgeOnHold;
    default:
      return '';
  }
};

// ============= Industry Icons =============

const industryIcons: Record<IndustryCode, string> = {
  FB: '🍽️',
  CL: '🧹',
  NR: '👩‍⚕️',
  HC: '🏥',
  HT: '🏨',
};

// ============= Workflow Dashboard Component =============

export const WorkflowDashboard: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [filters, setFilters] = useState<DashboardFilters>({
    industry: 'all',
    state: 'all',
    dateRange: 'today',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsData, templatesData, instancesData] = await Promise.all([
        workflowService.getStats(filters.industry === 'all' ? undefined : filters.industry),
        workflowService.getActiveTemplates(),
        workflowService.getInstances({
          state: filters.state === 'all' ? undefined : filters.state,
          limit: 20,
        }),
      ]);

      setStats(statsData);
      setTemplates(templatesData);
      setInstances(instancesData);
    } catch (err) {
      console.error('Failed to fetch workflow data:', err);
      setError('Không thể tải dữ liệu workflow. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [filters.industry, filters.state]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Render loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  // Stats card data
  const statCards = [
    { title: 'Tổng Workflow', value: stats?.total_instances || 0, icon: '📊', color: 'coffee' as StatCardColor },
    { title: 'Đang thực hiện', value: stats?.in_progress || 0, icon: '🔄', color: 'turmeric' as StatCardColor },
    { title: 'Hoàn thành hôm nay', value: stats?.completed_today || 0, icon: '✅', color: 'herb' as StatCardColor },
    { title: 'F&B', value: stats?.by_industry?.FB || 0, icon: '🍽️', color: 'chili' as StatCardColor },
    { title: 'Cleaning', value: stats?.by_industry?.CL || 0, icon: '🧹', color: 'sky' as StatCardColor },
  ];

  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.headerTitle}>Quản lý Workflow</h1>
              <p className={styles.headerSubtitle}>
                Sprint 11 - Generic 5-State Workflow Engine
              </p>
            </div>
            <div className={styles.headerActions}>
              <button
                onClick={() => navigate('/workflows/create')}
                className={styles.createButton}
              >
                + Tạo Workflow mới
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Error Alert */}
        {error && (
          <div className={styles.errorAlert}>
            {error}
            <button onClick={fetchData} className={styles.retryButton}>
              Thử lại
            </button>
          </div>
        )}

        {/* Filters */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersGrid}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Ngành</label>
              <select
                value={filters.industry}
                onChange={(e) => setFilters({ ...filters, industry: e.target.value as IndustryCode | 'all' })}
                className={styles.filterSelect}
              >
                <option value="all">Tất cả ngành</option>
                {Object.entries(INDUSTRY_LABELS).map(([code, labels]) => (
                  <option key={code} value={code}>
                    {labels.vi}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Trạng thái</label>
              <select
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value as WorkflowState | 'all' })}
                className={styles.filterSelect}
              >
                <option value="all">Tất cả trạng thái</option>
                {Object.entries(WORKFLOW_STATE_LABELS).map(([state, labels]) => (
                  <option key={state} value={state}>
                    {labels.vi}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Thời gian</label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as 'today' | 'week' | 'month' })}
                className={styles.filterSelect}
              >
                <option value="today">Hôm nay</option>
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className={styles.statsGrid}>
            {statCards.map((card, idx) => (
              <div key={idx} className={`${styles.statCard} ${getStatCardClass(card.color)}`}>
                <div className={styles.statCardContent}>
                  <span className={styles.statIcon}>{card.icon}</span>
                  <span className={styles.statValue}>{card.value}</span>
                </div>
                <p className={styles.statTitle}>{card.title}</p>
              </div>
            ))}
          </div>
        )}

        {/* State Distribution */}
        {stats && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Phân bố theo trạng thái</h2>
            <div className={styles.stateGrid}>
              {Object.entries(WORKFLOW_STATE_LABELS).map(([state, labels]) => {
                const count = stats.by_state?.[state as WorkflowState] || 0;
                return (
                  <div
                    key={state}
                    className={`${styles.stateCard} ${getStateCardClass(state as WorkflowState)}`}
                    onClick={() => setFilters({ ...filters, state: state as WorkflowState })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setFilters({ ...filters, state: state as WorkflowState });
                      }
                    }}
                  >
                    <div className={styles.stateValue}>{count}</div>
                    <div className={styles.stateLabel}>{labels.vi}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Templates List */}
        <div className={styles.templatesSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Workflow Templates ({templates.length})</h2>
          </div>
          <div className={styles.sectionContent}>
            {templates.length === 0 ? (
              <p className={styles.emptyState}>Chưa có template nào</p>
            ) : (
              <div className={styles.templatesGrid}>
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={styles.templateCard}
                    onClick={() => navigate(`/workflows/create?template=${template.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/workflows/create?template=${template.id}`);
                      }
                    }}
                  >
                    <div className={styles.templateHeader}>
                      <span className={styles.templateIcon}>{industryIcons[template.industry]}</span>
                      <span className={styles.templateBccPrefix}>{template.bcc_prefix}</span>
                    </div>
                    <h3 className={styles.templateName}>
                      {template.display_name_vi || template.display_name}
                    </h3>
                    <p className={styles.templateDescription}>{template.description}</p>
                    <div className={styles.templateFooter}>
                      <span className={styles.templateIndustry}>
                        {getIndustryLabel(template.industry)}
                      </span>
                      {template.is_active && (
                        <span className={styles.templateActiveBadge}>Active</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Instances */}
        <div className={styles.instancesSection}>
          <div className={styles.sectionHeaderWithAction}>
            <h2 className={styles.sectionTitle}>Workflow gần đây</h2>
            <button
              onClick={() => navigate('/workflows/instances')}
              className={styles.viewAllLink}
            >
              Xem tất cả →
            </button>
          </div>
          <div className={styles.tableContainer}>
            {instances.length === 0 ? (
              <p className={styles.emptyState}>Chưa có workflow nào</p>
            ) : (
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.th}>Mã BCC</th>
                    <th className={styles.th}>Trạng thái</th>
                    <th className={styles.th}>Ngày tạo</th>
                    <th className={styles.th}>Thao tác</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {instances.map((instance) => (
                    <tr key={instance.id}>
                      <td className={styles.td}>
                        <span className={styles.bccCode}>{instance.bcc_code}</span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.stateBadge} ${getStateBadgeClass(instance.current_state)}`}>
                          {getStateLabel(instance.current_state)}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={styles.dateText}>
                          {new Date(instance.created_at).toLocaleDateString('vi-VN')}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <button
                          onClick={() => navigate(`/workflows/instances/${instance.id}`)}
                          className={styles.detailLink}
                        >
                          Chi tiết
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WorkflowDashboard;
